import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin, ShoppingCart, Download, LogIn } from "lucide-react";
import { Instagram, Facebook } from "lucide-react";
import Cart from "./Cart";
import ComingSoonModal from "./ComingSoonModal";
import { getCartCount } from "../../utils/CartUtils";
import { useNotification } from "../../context/NotificationContext";
import UserCatalogService from "../../services/user/UserCatalogService";
import { useUserAuth } from "../../services/user/UserAuthServices";



export default function Header() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  // const [cartCount] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [comingSoonModal, setComingSoonModal] = useState({
    isOpen: false,
    featureName: "",
  });
  const { showNotification } = useNotification();
  const [isDownloading, setIsDownloading] = useState(false);
  const { user } = useUserAuth();



  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Update cart count using utility
  useEffect(() => {
    const updateCount = () => {
      setCartCount(getCartCount());
    };

    updateCount();
    window.addEventListener("storage", updateCount);
    window.addEventListener("cartUpdated", updateCount);

    return () => {
      window.removeEventListener("storage", updateCount);
      window.removeEventListener("cartUpdated", updateCount);
    };
  }, []);


  const navItems = [
    { name: "Home", href: "/" },
    { name: "About Us", href: null, featureName: "About Page" },
    { name: "Rentals", href: "/rentals", featureName: "Rentals Catalog" },
    // { name: "Events", href: "/events" },
    { name: "Gallery", href: "/gallery", featureName: "Gallery" },
    // { name: "Dashboard", href: "/dashboard" }, // Removed Dashboard link
    { name: "Insights", href: null, featureName: "Insights Blog" },
    { name: "Contact", href: "/contact" },
  ];

  const handleNavClick = (e, item) => {
    if (!item.href) {
      e.preventDefault();
      setComingSoonModal({ isOpen: true, featureName: item.featureName });
      setIsMenuOpen(false);
    }
  };

  const handleDownloadCatalog = async (e) => {
    e.preventDefault();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await UserCatalogService.downloadCatalog();

      // Axios response data contains the blob
      const blob = response.data || response;

      if (!blob || blob.size === 0 || blob.type === 'application/json') {
        // Handle empty or JSON error response from blob endpoint
        showNotification({
          type: "info",
          title: "Catalog Unavailable",
          message: "No catalog uploaded at the moment. Please check back later."
        });
        setIsDownloading(false);
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'St_Stephens_Catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        type: "success",
        title: "Download Started",
        message: "The catalog is being downloaded to your device."
      });
    } catch (err) {
      console.error("Download error:", err);
      showNotification({
        type: "failed",
        title: "Download Failed",
        message: "Could not download the catalog. Please try again later."
      });
    } finally {
      setIsDownloading(false);
      setIsMenuOpen(false);
    }
  };


  return (
    <>
      {/* Top Utility Bar - Scrolls Away naturally */}
      <div
        className="bg-white dark:bg-[#1E1E1E] border-b border-[#F0F0F0] dark:border-[#333333] font-sans"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Left Side - Address & Social */}
            <div className="flex flex-col gap-2 w-full sm:w-auto">
              <Link
                to="https://maps.app.goo.gl/m9iAyeNt89fG56u46"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#333333] dark:text-[#E0E0E0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-150"
              >
                <MapPin size={14} />
                <span>8 shaki crescent St, Surulere, Lagos 101241, Lagos, Nigeria</span>
              </Link>
              <div className="flex items-center gap-3">
                <Link
                  to="https://www.instagram.com/st.stephens.rentals/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#333333] dark:text-[#E0E0E0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-150"
                  aria-label="Instagram"
                >
                  <Instagram size={16} />
                </Link>
                <Link
                  to="https://www.facebook.com/p/Ststephens-events-100064674373060/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#333333] dark:text-[#E0E0E0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-150"
                  aria-label="Facebook"
                >
                  <Facebook size={16} />
                </Link>
              </div>
            </div>

            {/* Right Side - Enhanced Catalog CTA */}
            <button
              onClick={handleDownloadCatalog}
              disabled={isDownloading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#62397f] hover:bg-[#7C3AED] text-white text-sm font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] shadow-[0_4px_12px_rgba(139,92,246,0.2)] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Download size={16} className={isDownloading ? "animate-bounce" : ""} />
              <span>{isDownloading ? "Downloading..." : "Download Catalog"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Primary Navigation Bar - Stays Fixed */}
      <header
        className={`sticky top-0 z-50 bg-white dark:bg-[#1E1E1E] w-full transition-shadow duration-200 font-sans ${isScrolled
          ? "shadow-[0_2px_12px_rgba(139,92,246,0.08)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.3)]"
          : ""
          }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-lg flex items-center justify-center">
                <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" loading="eager" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-medium font-serif text-black dark:text-white leading-tight">
                  St. Stephen's
                </span>
                <span className="text-xs text-[#333333] dark:text-[#E0E0E0] leading-tight font-serif italic">
                  Rentals & Events
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => {
                const isActive = item.href === "/"
                  ? location.pathname === "/"
                  : item.href && (location.pathname.startsWith(item.href) || (item.name === "Rentals" && location.pathname.startsWith("/rental/")));

                return (
                  <Link
                    key={item.name}
                    to={item.href || "#"}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`font-serif text-[15px] font-bold transition-colors duration-150 tracking-wide ${isActive
                      ? "text-[#62397f] dark:text-[#A78BFA] border-b-2 border-[#62397f] dark:border-[#A78BFA] pb-1"
                      : "text-black dark:text-white hover:text-[#62397f] dark:hover:text-[#A78BFA]"
                      }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Right Side - Cart & Mobile Menu */}
            <div className="flex items-center gap-4">
              {/* Desktop Login/Dashboard Button */}
              <div className="hidden lg:flex items-center">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-5 py-2 rounded-full border border-[#62397f] dark:border-[#A78BFA] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#62397f] hover:text-white dark:hover:bg-[#A78BFA] dark:hover:text-[#1E1E1E] transition-all duration-150 text-sm font-medium"
                  >
                    <LogIn size={16} />
                    <span>Dashboard</span>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-5 py-2 rounded-full border border-[#E9E9E9] dark:border-[#333333] text-black dark:text-white hover:border-[#62397f] dark:hover:border-[#A78BFA] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-all duration-150 text-sm font-medium"
                  >
                    <LogIn size={16} />
                    <span>Login</span>
                  </Link>
                )}
              </div>


              {/* Cart */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] transition-all duration-150 relative"
              >
                <ShoppingCart size={18} />
                <span className="hidden sm:inline text-sm font-medium">
                  My Rentals
                </span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#62397f] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2 text-black dark:text-white hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-150"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                <div className="flex flex-col items-center gap-0.5">
                  {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
                  <span className="text-[10px] font-medium">Menu</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Off-Canvas Menu */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transform transition-transform duration-300 ease-out ${isMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${isMenuOpen ? "opacity-50" : "opacity-0"
            }`}
          onClick={() => setIsMenuOpen(false)}
        ></div>

        {/* Menu Panel */}
        <div className="relative w-[320px] h-full bg-white dark:bg-[#1E1E1E] shadow-2xl overflow-y-auto">
          <div className="p-6">
            {/* Header with Close button and Logo */}
            <div className="flex items-center justify-between mb-8">
              {/* Logo - Left */}
              <Link
                to="/"
                className="flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center pl-3">
                  <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" className="w-full h-full object-contain scale-[2.25] transition-transform duration-200" />
                </div>
              </Link>

              {/* Close button - Right */}
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-black dark:text-white hover:text-[#404040] dark:hover:text-gray-300 transition-colors duration-200 p-2 -mr-2 touch-manipulation"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            {/* Navigation Links */}
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => {
                const isActive = item.href === "/"
                  ? location.pathname === "/"
                  : item.href && (location.pathname.startsWith(item.href) || (item.name === "Rentals" && location.pathname.startsWith("/rental/")));

                return (
                  <Link
                    key={item.name}
                    to={item.href || "#"}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`font-serif text-base font-bold transition-colors duration-150 py-3 px-3 rounded-lg flex items-center justify-between tracking-wide ${isActive
                      ? "bg-purple-50 dark:bg-purple-900/10 text-[#62397f] dark:text-[#A78BFA] font-bold"
                      : "text-black dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      }`}
                  >
                    {item.name}
                    {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#62397f] dark:bg-[#A78BFA]"></div>}
                  </Link>
                );
              })}

              {/* Mobile Login/Dashboard Button */}
              {user ? (
                <Link
                  to="/dashboard"
                  onClick={() => setIsMenuOpen(false)}
                  className="mt-2 flex items-center gap-3 px-5 py-3.5 rounded-xl border border-[#62397f] dark:border-[#A78BFA] text-[#62397f] dark:text-[#A78BFA] hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all duration-150"
                >
                  <LogIn size={20} className="text-[#62397f] dark:text-[#A78BFA]" />
                  <span className="font-semibold text-base">Dashboard</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="mt-2 flex items-center gap-3 px-5 py-3.5 rounded-xl border border-[#E9E9E9] dark:border-[#333333] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-150"
                >
                  <LogIn size={20} className="text-[#62397f] dark:text-[#A78BFA]" />
                  <span className="font-semibold text-base">Login / Account</span>
                </Link>
              )}


              {/* Cart Link in Mobile */}
              <button
                onClick={() => {
                  setIsCartOpen(true);
                  setIsMenuOpen(false);
                }}
                className="mt-4 flex items-center gap-3 px-5 py-3.5 rounded-xl bg-[#62397f] text-white hover:bg-[#7C3AED] transition-all duration-150 w-full"
              >
                <ShoppingCart size={20} />
                <span className="font-semibold text-base">My Rentals</span>
                {cartCount > 0 && (
                  <span className="ml-auto bg-white text-[#62397f] text-sm font-bold px-2.5 py-1 rounded-full">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Download Catalog - Mobile */}
              <button
                onClick={handleDownloadCatalog}
                disabled={isDownloading}
                className="mt-2 flex items-center gap-3 px-5 py-3.5 rounded-xl bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] transition-all duration-150 disabled:opacity-70"
              >
                <Download size={20} className={isDownloading ? "animate-bounce" : ""} />
                <span className="font-semibold text-base">
                  {isDownloading ? "Downloading..." : "Download Catalog"}
                </span>
              </button>

              {/* Divider */}
              <div className="my-4 border-t border-[#E9E9E9] dark:border-[#333333]"></div>

              {/* Address */}
              <div className="px-2 py-3">
                <Link
                  to="https://maps.app.goo.gl/m9iAyeNt89fG56u46"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm text-[#333333] dark:text-[#E0E0E0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-150"
                >
                  <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                  <span>8 shaki crescent St, Surulere, Lagos 101241, Lagos, Nigeria</span>
                </Link>
              </div>

              {/* Social Media */}
              <div className="px-2 py-2">
                <p className="text-xs font-semibold text-[#999999] dark:text-[#666666] uppercase tracking-wider mb-3">
                  Follow Us
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    to="https://www.instagram.com/st.stephens.rentals/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-[#F5F3FF] dark:bg-[#2A2440] hover:bg-[#62397f] dark:hover:bg-[#62397f] text-[#62397f] dark:text-[#A78BFA] hover:text-white dark:hover:text-white rounded-full flex items-center justify-center transition-all duration-200"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </Link>
                  <Link
                    to="https://www.facebook.com/p/Ststephens-events-100064674373060/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-[#F5F3FF] dark:bg-[#2A2440] hover:bg-[#62397f] dark:hover:bg-[#62397f] text-[#62397f] dark:text-[#A78BFA] hover:text-white dark:hover:text-white rounded-full flex items-center justify-center transition-all duration-200"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </Link>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Cart Drawer */}
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, featureName: "" })}
        featureName={comingSoonModal.featureName}
      />
    </>
  );
}
